import {
  type TenantAnalyticsAdapter,
  type TenantAnalyticsConsentSnapshot,
  type TenantAnalyticsEnvironment,
  type TenantMarketingProvider,
  isTenantAnalyticsDocumentLocationSafe,
  sanitizeTenantAnalyticsPath,
  sanitizeTenantAnalyticsReferrer,
} from '@kit/analytics/tenant';
import {
  type GoogleAdsPurchaseParameters,
  type GoogleAnalytics4EventParameters,
  type GoogleTagManagerDataLayerEvent,
  type TenantGoogleAnalyticsIntegrationSelection,
  createGoogleAdsAdapter,
  createGoogleAnalytics4Adapter,
  createGoogleTagManagerAdapter,
  parseGoogleAdsPublicConfig,
  parseGoogleAnalytics4PublicConfig,
  parseGoogleTagManagerPublicConfig,
  selectTenantGoogleAnalyticsIntegration,
} from '@kit/analytics/tenant/google';

export type TenantAnalyticsClientJsonValue =
  | boolean
  | number
  | string
  | null
  | TenantAnalyticsClientJsonObject
  | readonly TenantAnalyticsClientJsonValue[];

export interface TenantAnalyticsClientJsonObject {
  readonly [key: string]: TenantAnalyticsClientJsonValue;
}

export interface TenantAnalyticsClientIntegration {
  readonly provider: TenantMarketingProvider;
  readonly environment: TenantAnalyticsEnvironment;
  readonly publicConfig: TenantAnalyticsClientJsonObject;
  readonly configVersion: number;
}

export interface TenantAnalyticsClientConfiguration {
  readonly environment: TenantAnalyticsEnvironment | null;
  readonly integrations: readonly TenantAnalyticsClientIntegration[];
}

interface TenantGoogleTransportBase {
  readonly environment: TenantAnalyticsEnvironment;
  readonly configVersion: number;
  readonly key: string;
  readonly dataLayerName: string;
  readonly cookiePrefix: string;
}

export type TenantGoogleTransport = TenantGoogleTransportBase &
  TenantGoogleAnalyticsIntegrationSelection;

export interface TenantGoogleScriptDescriptor {
  readonly id: string;
  readonly src: string;
}

type GoogleConsentValue = 'denied' | 'granted';

export interface GoogleConsentModeState {
  readonly analytics_storage: GoogleConsentValue;
  readonly ad_storage: GoogleConsentValue;
  readonly ad_user_data: GoogleConsentValue;
  readonly ad_personalization: GoogleConsentValue;
  readonly wait_for_update?: number;
}

export type TenantGoogleDataLayerEntry =
  | IArguments
  | GoogleTagManagerDataLayerEvent
  | { readonly ecommerce: null }
  | {
      readonly event: 'gtm.js';
      readonly 'gtm.start': number;
    };

type TenantGoogleDataLayer = TenantGoogleDataLayerEntry[];

interface ActivatedTransportState {
  readonly key: string;
  analyticsGranted: boolean;
  advertisingGranted: boolean;
  ready: boolean;
  readonly configuredDestinationIds: Set<string>;
}

const activationRegistry = new WeakMap<
  Window,
  Map<string, ActivatedTransportState>
>();
const activationListeners = new WeakMap<Window, Map<string, Set<() => void>>>();
let fallbackEventSequence = 0;

const DEFAULT_DENIED_CONSENT = Object.freeze({
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  wait_for_update: 500,
}) satisfies GoogleConsentModeState;

const ANALYTICS_ONLY_GRANTED_CONSENT = Object.freeze({
  analytics_storage: 'granted',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
}) satisfies GoogleConsentModeState;

const ADVERTISING_ONLY_GRANTED_CONSENT = Object.freeze({
  analytics_storage: 'denied',
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
}) satisfies GoogleConsentModeState;

const ANALYTICS_AND_ADVERTISING_GRANTED_CONSENT = Object.freeze({
  analytics_storage: 'granted',
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
}) satisfies GoogleConsentModeState;

const ALL_DENIED_CONSENT = Object.freeze({
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
}) satisfies GoogleConsentModeState;

export function selectTenantGoogleTransport(
  tenantId: string,
  configuration: TenantAnalyticsClientConfiguration,
): TenantGoogleTransport | null {
  const environment = configuration.environment;

  if (!environment || tenantId.trim().length === 0) {
    return null;
  }

  const integrations = configuration.integrations.filter(
    (integration) =>
      integration.environment === environment &&
      Number.isInteger(integration.configVersion) &&
      integration.configVersion > 0,
  );
  const selection = selectTenantGoogleAnalyticsIntegration(
    integrations.map((integration) => ({
      provider: integration.provider,
      enabled: true,
      publicConfig: integration.publicConfig,
    })),
  );

  if (!selection) {
    return null;
  }

  const providerId =
    selection.provider === 'google_tag_manager'
      ? selection.publicConfig.containerId
      : [
          selection.googleAnalytics4?.measurementId,
          selection.googleAds?.conversionId,
        ]
          .filter(Boolean)
          .join('+');
  const selectedVersions = integrations
    .filter((integration) => matchesSelection(integration, selection))
    .map((integration) => integration.configVersion);

  if (selectedVersions.length === 0) {
    return null;
  }

  const configVersion = Math.max(...selectedVersions);
  const key = [
    tenantId.trim(),
    environment,
    selection.provider,
    providerId,
    selectedVersions.sort((left, right) => left - right).join('.'),
  ].join(':');
  const dataLayerName = createTenantGoogleDataLayerName(key);
  const stableCookieKey = [
    tenantId.trim(),
    environment,
    selection.provider,
    providerId,
  ].join(':');
  const cookieNamespace = createTenantGoogleDataLayerName(stableCookieKey);

  return Object.freeze({
    ...selection,
    environment,
    configVersion,
    key,
    dataLayerName,
    cookiePrefix: `ta_${cookieNamespace.slice('tenantAnalytics_'.length)}`,
  });
}

export function getTenantGoogleScriptDescriptor(
  transport: TenantGoogleTransport,
): TenantGoogleScriptDescriptor {
  const dataLayer = encodeURIComponent(transport.dataLayerName);

  if (transport.provider === 'google_tag_manager') {
    const containerId = encodeURIComponent(transport.publicConfig.containerId);

    return Object.freeze({
      id: `tenant-google-tag-manager-${transport.dataLayerName}`,
      src: `https://www.googletagmanager.com/gtm.js?id=${containerId}&l=${dataLayer}`,
    });
  }

  const destinationId = encodeURIComponent(
    transport.googleAnalytics4?.measurementId ??
      requireGoogleAdsConfig(transport).conversionId,
  );

  return Object.freeze({
    id: `tenant-google-analytics-4-${transport.dataLayerName}`,
    src: `https://www.googletagmanager.com/gtag/js?id=${destinationId}&l=${dataLayer}`,
  });
}

export function getConsentGatedTenantGoogleScriptDescriptor(
  transport: TenantGoogleTransport | null,
  consent: TenantAnalyticsConsentSnapshot,
  isTransportActivated: boolean,
) {
  if (
    !transport ||
    !isTransportActivated ||
    !hasRequiredTenantGoogleConsent(transport, consent)
  ) {
    return null;
  }

  return getTenantGoogleScriptDescriptor(transport);
}

/**
 * Activates Google's basic consent mode synchronously before a script is
 * rendered. Analytics consent activates the transport; the advertising choice
 * is expressed through the Consent Mode update, and later consent changes push
 * a fresh update rather than reloading anything.
 */
export function activateTenantGoogleTransport(
  transport: TenantGoogleTransport,
  consent: TenantAnalyticsConsentSnapshot,
) {
  if (!hasRequiredTenantGoogleConsent(transport, consent)) {
    return false;
  }

  const registry = getActivationRegistry();
  const current = registry.get(transport.dataLayerName);
  const dataLayer = getTenantGoogleDataLayer(transport.dataLayerName);
  const advertisingGranted = consent.advertising === true;

  if (current?.key === transport.key) {
    if (
      current.analyticsGranted !== (consent.analytics === true) ||
      current.advertisingGranted !== advertisingGranted
    ) {
      pushGoogleCommand(
        dataLayer,
        'consent',
        'update',
        getGrantedConsentModeState(consent),
      );
      current.analyticsGranted = consent.analytics === true;
      current.advertisingGranted = advertisingGranted;
      notifyActivationChange(transport.key);
    }

    configureDirectDestinations(transport, consent, dataLayer, current);

    return true;
  }

  pushGoogleCommand(dataLayer, 'consent', 'default', DEFAULT_DENIED_CONSENT);
  pushGoogleCommand(
    dataLayer,
    'consent',
    'update',
    getGrantedConsentModeState(consent),
  );
  const privacySettings = Object.freeze({
    cookie_domain: 'none',
    cookie_prefix: transport.cookiePrefix,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  pushGoogleCommand(dataLayer, 'set', privacySettings);

  const state: ActivatedTransportState = {
    key: transport.key,
    analyticsGranted: consent.analytics === true,
    advertisingGranted,
    ready: false,
    configuredDestinationIds: new Set(),
  };

  if (transport.provider === 'google_tag_manager') {
    dataLayer.push({
      'gtm.start': Date.now(),
      event: 'gtm.js',
    });
  } else {
    pushGoogleCommand(dataLayer, 'js', new Date());
    configureDirectDestinations(transport, consent, dataLayer, state);
  }

  registry.set(transport.dataLayerName, state);
  notifyActivationChange(transport.key);

  return true;
}

export function denyTenantGoogleTransport(transport: TenantGoogleTransport) {
  const current = getActivationRegistry().get(transport.dataLayerName);

  if (
    current?.key !== transport.key ||
    (!current.analyticsGranted && !current.advertisingGranted)
  ) {
    return;
  }

  pushGoogleCommand(
    getTenantGoogleDataLayer(transport.dataLayerName),
    'consent',
    'update',
    ALL_DENIED_CONSENT,
  );
  current.analyticsGranted = false;
  current.advertisingGranted = false;
  notifyActivationChange(transport.key);
}

export function isTenantGoogleTransportActivated(
  transport: TenantGoogleTransport,
) {
  const current = getActivationRegistry().get(transport.dataLayerName);

  return (
    current?.key === transport.key &&
    (current.analyticsGranted || current.advertisingGranted)
  );
}

export function isTenantGoogleTransportReady(transport: TenantGoogleTransport) {
  const current = getActivationRegistry().get(transport.dataLayerName);

  return current?.key === transport.key && current.ready === true;
}

export function setTenantGoogleTransportReady(
  transport: TenantGoogleTransport,
  ready: boolean,
) {
  const current = getActivationRegistry().get(transport.dataLayerName);

  if (current?.key !== transport.key || current.ready === ready) {
    return;
  }

  current.ready = ready;
  notifyActivationChange(transport.key);
}

export function subscribeToTenantGoogleTransportActivation(
  transport: TenantGoogleTransport,
  onStoreChange: () => void,
) {
  const listeners = getActivationListeners();
  const transportListeners = listeners.get(transport.key) ?? new Set();

  transportListeners.add(onStoreChange);
  listeners.set(transport.key, transportListeners);

  return () => {
    transportListeners.delete(onStoreChange);

    if (transportListeners.size === 0) {
      listeners.delete(transport.key);
    }
  };
}

export function createTenantGoogleBrowserAdapters(
  transport: TenantGoogleTransport,
  pageOrigin: string,
): readonly TenantAnalyticsAdapter[] {
  if (transport.provider === 'google_tag_manager') {
    return Object.freeze([
      createGoogleTagManagerAdapter({
        publicConfig: transport.publicConfig,
        id: `tenant:${transport.key}`,
        push: (event) => {
          const dataLayer = getTenantGoogleDataLayer(transport.dataLayerName);

          if ('ecommerce' in event) {
            dataLayer.push({ ecommerce: null });
          }

          dataLayer.push(event);
        },
      }),
    ]);
  }

  const adapters: TenantAnalyticsAdapter[] = [];

  if (transport.googleAnalytics4) {
    adapters.push(
      createGoogleAnalytics4Adapter({
        publicConfig: transport.googleAnalytics4,
        pageOrigin,
        id: `tenant:${transport.key}:ga4`,
        send: (eventName, parameters) => {
          pushGoogleAnalyticsEvent(
            getTenantGoogleDataLayer(transport.dataLayerName),
            eventName,
            parameters,
          );
        },
      }),
    );
  }

  if (transport.googleAds) {
    adapters.push(
      createGoogleAdsAdapter({
        publicConfig: transport.googleAds,
        id: `tenant:${transport.key}:ads`,
        send: (eventName, parameters) => {
          pushGoogleAdsEvent(
            getTenantGoogleDataLayer(transport.dataLayerName),
            eventName,
            parameters,
          );
        },
      }),
    );
  }

  return Object.freeze(adapters);
}

/** @deprecated Prefer the plural API; retained for single-destination callers. */
export function createTenantGoogleBrowserAdapter(
  transport: TenantGoogleTransport,
  pageOrigin: string,
) {
  const adapter = createTenantGoogleBrowserAdapters(transport, pageOrigin)[0];

  if (!adapter) {
    throw new Error('Tenant Google transport has no adapter.');
  }

  return adapter;
}

export function createTenantAnalyticsEventId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  fallbackEventSequence += 1;

  return `${Date.now().toString(36)}-${fallbackEventSequence.toString(36)}`;
}

export function createTenantAnalyticsPagePath(
  pathname: string,
  serializedSearchParameters: string,
) {
  const search = serializedSearchParameters.startsWith('?')
    ? serializedSearchParameters.slice(1)
    : serializedSearchParameters;

  return sanitizeTenantAnalyticsPath(
    search.length > 0 ? `${pathname}?${search}` : pathname,
  );
}

export function isTenantAnalyticsBrowserDocumentLocationSafe(
  location: string,
  referrer: string,
) {
  return isTenantAnalyticsDocumentLocationSafe(location, referrer);
}

export function createTenantAnalyticsInitialReferrer(referrer: string) {
  return sanitizeTenantAnalyticsReferrer(referrer);
}

export function createTenantAnalyticsSpaReferrer(
  previousPath: string,
  pageOrigin: string,
) {
  try {
    const origin = new URL(pageOrigin);

    if (
      origin.origin !== pageOrigin ||
      (origin.protocol !== 'https:' && origin.protocol !== 'http:')
    ) {
      return null;
    }

    const candidate = new URL(previousPath, origin);

    if (candidate.origin !== origin.origin) {
      return null;
    }

    return sanitizeTenantAnalyticsReferrer(candidate.toString());
  } catch {
    return null;
  }
}

export function hasRequiredTenantGoogleConsent(
  transport: TenantGoogleTransport,
  consent: TenantAnalyticsConsentSnapshot,
) {
  if (consent.necessary !== true) {
    return false;
  }

  if (transport.provider === 'google_tag_manager') {
    // A tenant-managed container can contain arbitrary marketing tags. Unlike
    // direct destinations, the platform cannot prove that every tag declares
    // its Consent Mode requirements, so loading the container requires both
    // non-essential categories.
    return consent.analytics === true && consent.advertising === true;
  }

  return (
    (transport.googleAnalytics4 !== null && consent.analytics === true) ||
    (transport.googleAds !== null && consent.advertising === true)
  );
}

export function readTenantGoogleDataLayer(
  transport: TenantGoogleTransport,
): readonly TenantGoogleDataLayerEntry[] {
  return getTenantGoogleDataLayer(transport.dataLayerName);
}

function matchesSelection(
  integration: TenantAnalyticsClientIntegration,
  selection: TenantGoogleAnalyticsIntegrationSelection,
) {
  if (selection.provider === 'google_tag_manager') {
    if (integration.provider !== 'google_tag_manager') {
      return false;
    }

    return (
      parseGoogleTagManagerPublicConfig(integration.publicConfig)
        ?.containerId === selection.publicConfig.containerId
    );
  }

  if (integration.provider === 'google_analytics_4') {
    return (
      parseGoogleAnalytics4PublicConfig(integration.publicConfig)
        ?.measurementId === selection.googleAnalytics4?.measurementId
    );
  }

  if (integration.provider === 'google_ads') {
    const config = parseGoogleAdsPublicConfig(integration.publicConfig);

    return (
      config?.conversionId === selection.googleAds?.conversionId &&
      config?.conversionLabel === selection.googleAds?.conversionLabel
    );
  }

  return false;
}

function getGrantedConsentModeState(consent: TenantAnalyticsConsentSnapshot) {
  if (consent.analytics === true && consent.advertising === true) {
    return ANALYTICS_AND_ADVERTISING_GRANTED_CONSENT;
  }

  if (consent.analytics === true) {
    return ANALYTICS_ONLY_GRANTED_CONSENT;
  }

  return ADVERTISING_ONLY_GRANTED_CONSENT;
}

function configureDirectDestinations(
  transport: TenantGoogleTransport,
  consent: TenantAnalyticsConsentSnapshot,
  dataLayer: TenantGoogleDataLayer,
  state: ActivatedTransportState,
) {
  if (transport.provider !== 'google_direct') {
    return;
  }

  const privacySettings = Object.freeze({
    cookie_domain: 'none',
    cookie_prefix: transport.cookiePrefix,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  if (
    consent.analytics === true &&
    transport.googleAnalytics4 &&
    !state.configuredDestinationIds.has(
      transport.googleAnalytics4.measurementId,
    )
  ) {
    pushGoogleCommand(
      dataLayer,
      'config',
      transport.googleAnalytics4.measurementId,
      Object.freeze({ send_page_view: false, ...privacySettings }),
    );
    state.configuredDestinationIds.add(
      transport.googleAnalytics4.measurementId,
    );
  }

  if (
    consent.advertising === true &&
    transport.googleAds &&
    !state.configuredDestinationIds.has(transport.googleAds.conversionId)
  ) {
    pushGoogleCommand(
      dataLayer,
      'config',
      transport.googleAds.conversionId,
      Object.freeze({ send_page_view: false, ...privacySettings }),
    );
    state.configuredDestinationIds.add(transport.googleAds.conversionId);
  }
}

function requireGoogleAdsConfig(transport: TenantGoogleTransport) {
  if (transport.provider !== 'google_direct' || !transport.googleAds) {
    throw new Error('Google direct transport has no destination.');
  }

  return transport.googleAds;
}

function createTenantGoogleDataLayerName(key: string) {
  let hash = 2_166_136_261;

  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }

  return `tenantAnalytics_${(hash >>> 0).toString(36)}`;
}

function getActivationRegistry() {
  const current = activationRegistry.get(window);

  if (current) {
    return current;
  }

  const registry = new Map<string, ActivatedTransportState>();
  activationRegistry.set(window, registry);

  return registry;
}

function getActivationListeners() {
  const current = activationListeners.get(window);

  if (current) {
    return current;
  }

  const listeners = new Map<string, Set<() => void>>();
  activationListeners.set(window, listeners);

  return listeners;
}

function notifyActivationChange(key: string) {
  getActivationListeners()
    .get(key)
    ?.forEach((listener) => listener());
}

function getTenantGoogleDataLayer(name: string): TenantGoogleDataLayer {
  const browserWindow = window as unknown as Record<string, unknown>;
  const existing = browserWindow[name];

  if (Array.isArray(existing)) {
    return existing as TenantGoogleDataLayer;
  }

  const dataLayer: TenantGoogleDataLayer = [];
  browserWindow[name] = dataLayer;

  return dataLayer;
}

function pushGoogleCommand(
  dataLayer: TenantGoogleDataLayer,
  ...command: readonly unknown[]
) {
  dataLayer.push(captureGoogleTagArguments(...command));
}

/** Mirrors Google's official `gtag(){ dataLayer.push(arguments) }` queue. */
function captureGoogleTagArguments(
  ..._command: readonly unknown[]
): IArguments {
  // eslint-disable-next-line prefer-rest-params -- gtag queues IArguments.
  return arguments;
}

function pushGoogleAnalyticsEvent(
  dataLayer: TenantGoogleDataLayer,
  eventName: string,
  parameters: GoogleAnalytics4EventParameters,
) {
  pushGoogleCommand(dataLayer, 'event', eventName, parameters);
}

function pushGoogleAdsEvent(
  dataLayer: TenantGoogleDataLayer,
  eventName: 'conversion',
  parameters: GoogleAdsPurchaseParameters,
) {
  pushGoogleCommand(dataLayer, 'event', eventName, parameters);
}
